// components/RichContent.tsx
import { makeStyles } from '@material-ui/core';
import DOMPurify from 'dompurify';
import React, { useEffect, useMemo, useState } from 'react';
import { MentionType } from '../../api/mentions';
import Mention from './mention';

const useStyles = makeStyles(() => ({
  mention: {
    fontSize: '14px',
  },
}));

type Props = {
  contents: string;
  className?: string;
  /** Set the outer wrapper element to avoid invalid nesting inside <p> if needed */
  wrapper?: keyof JSX.IntrinsicElements; // e.g. 'div' | 'span'
  /** Optional DOMPurify config override */
  sanitizeOptions?: DOMPurify.Config;
};

/**
 * Renders sanitized HTML and replaces <a class="mention ..."> with <Mention />.
 * Uses only DOMPurify (v3.0.2 in your deps) + DOMParser, no extra libraries.
 * Handles void elements (br/img/hr/...) without children to avoid React warnings.
 */
const RichContent: React.FC<Props> = ({
  contents,
  className,
  wrapper = 'div',
  sanitizeOptions,
}) => {
  const [isClient, setIsClient] = useState(false);
  const classes = useStyles();

  const sanitized = useMemo(() => {
    const defaultOptions: DOMPurify.Config = { USE_PROFILES: { html: true } };
    return DOMPurify.sanitize(
      contents || '',
      sanitizeOptions ?? defaultOptions,
    );
  }, [contents, sanitizeOptions]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const nodes = useMemo(() => {
    if (!isClient || !sanitized) return null;

    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitized as string, 'text/html');
    const root = doc.body;

    const VOID_TAGS = new Set([
      'area',
      'base',
      'br',
      'col',
      'embed',
      'hr',
      'img',
      'input',
      'keygen',
      'link',
      'meta',
      'param',
      'source',
      'track',
      'wbr',
    ]);

    const walk = (node: Node): React.ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as HTMLElement;
        const tag = el.tagName.toLowerCase();

        // Replace <a class="mention ..."> with <Mention />
        if (tag === 'a' && el.classList.contains('mention')) {
          const id = el.getAttribute('data-mention-id') ?? '';
          const mentionType = el.getAttribute('data-mention-type') ?? '';
          const displayName = el.textContent ?? '';
          return (
            <Mention
              id={id}
              mentionType={mentionType as MentionType}
              displayName={displayName}
              className={classes.mention}
            />
          );
        }

        const props: Record<string, any> = {};
        for (const { name, value } of Array.from(el.attributes)) {
          if (name === 'class') props.className = value;
          else if (name === 'style') props.style = inlineStyleToObject(value);
          else if (/^data-/.test(name)) props[name] = value;
          else if (['href', 'target', 'rel', 'title', 'alt'].includes(name))
            props[name] = value;
          // Skip event handlers and unsafe attributes by default (already sanitized).
        }

        if (VOID_TAGS.has(tag)) {
          return React.createElement(tag, props);
        }

        const children = Array.from(el.childNodes).map((c, i) => (
          <React.Fragment key={i}>{walk(c)}</React.Fragment>
        ));

        return React.createElement(tag, props, children);
      }

      return null;
    };

    return Array.from(root.childNodes).map((n, i) => (
      <React.Fragment key={i}>{walk(n)}</React.Fragment>
    ));
  }, [isClient, sanitized, classes.mention]);

  // SSR-safe fallback: render sanitized HTML; on client we swap to real React nodes
  const Wrapper = wrapper as any;
  if (!isClient) {
    return (
      <Wrapper
        className={className}
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  return <Wrapper className={className}>{nodes}</Wrapper>;
};

// minimal inline style parser
function inlineStyleToObject(style: string) {
  return style
    .split(';')
    .map((s) => s.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, decl) => {
      const idx = decl.indexOf(':');
      if (idx === -1) return acc;
      const prop = decl.slice(0, idx).trim();
      const val = decl.slice(idx + 1).trim();
      const key = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
      if (prop) acc[key] = val;
      return acc;
    }, {});
}

export default RichContent;
